import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { ArrowLeft, Search, FileText, Paperclip, CheckCircle2, Clock, HelpCircle, AlertCircle } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { CorrectionsHistory } from "@/components/CorrectionsHistory";
import PhotoGallery from "@/components/PhotoGallery";

export default function SupervisorDataViewer() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  
  const [wellFilter, setWellFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLogId, setSelectedLogId] = useState<number | null>(null);
  
  // Monthly Review Mode state
  const [monthlyReviewMode, setMonthlyReviewMode] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [quickFilter, setQuickFilter] = useState<string | null>(null);
  const [chlorineQuickFilter, setChlorineQuickFilter] = useState<string | null>(null);
  const [reviewNote, setReviewNote] = useState("");
  
  // TRPC queries first (before dependent computations)
  const { data: wells } = trpc.wells.list.useQuery({ activeOnly: true });
  const { data: logs } = trpc.dailyLogs.listForViewer.useQuery({
    wellId: wellFilter && wellFilter !== "all" ? parseInt(wellFilter) : undefined,
    status: statusFilter !== "all" ? (statusFilter as any) : undefined,
    startDate: startDate ? new Date(startDate) : undefined,
    endDate: endDate ? new Date(endDate) : undefined,
    search: searchTerm || undefined,
    limit: 200,
  });

  const { data: selectedLog } = trpc.dailyLogs.getById.useQuery(
    { id: selectedLogId! },
    { enabled: selectedLogId !== null }
  );
  
  // TRPC utils and mutation (after queries)
  const utils = trpc.useUtils();
  
  const markReviewedMutation = trpc.dailyLogs.markReviewed.useMutation({
    onSuccess: () => {
      utils.dailyLogs.listForViewer.invalidate();
      if (selectedLogId) {
        utils.dailyLogs.getById.invalidate({ id: selectedLogId });
      }
      setReviewNote("");
    },
  });
  
  // Computed values (depend on queries)
  const getFilteredLogs = () => {
    if (!logs) return [];
    let filtered = logs;
    
    if (quickFilter) {
      switch (quickFilter) {
        case 'needs-review':
          filtered = logs.filter((l: any) => !l.reviewed);
          break;
        case 'reviewed':
          filtered = logs.filter((l: any) => l.reviewed);
          break;
        case 'has-corrections':
          filtered = logs.filter((l: any) => l.correctionsCount > 0);
          break;
        case 'missing-attachments':
          filtered = logs.filter((l: any) => l.attachmentsCount === 0);
          break;
        case 'locked':
          filtered = logs.filter((l: any) => l.status === 'locked');
          break;
      }
    }
    
    if (chlorineQuickFilter) {
      switch (chlorineQuickFilter) {
        case 'has-chlorine':
          filtered = filtered.filter(hasChlorine);
          break;
        case 'missing-residual':
          filtered = filtered.filter(isMissingResidual);
          break;
        case 'missing-cl-calc':
          filtered = filtered.filter(isMissingClCalc);
          break;
      }
    }
    
    return filtered;
  };
  
  const filteredLogs = getFilteredLogs();
  
  const reviewSummary = filteredLogs ? {
    total: filteredLogs.length,
    reviewed: filteredLogs.filter((l: any) => l.reviewed).length,
    remaining: filteredLogs.filter((l: any) => !l.reviewed).length,
    hasCorrections: filteredLogs.filter((l: any) => l.correctionsCount > 0).length,
    missingAttachments: filteredLogs.filter((l: any) => l.attachmentsCount === 0).length,
    lockedCorrected: filteredLogs.filter((l: any) => l.status === 'locked' && l.correctionsCount > 0).length,
  } : null;
  
  // Handlers (depend on states)
  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    if (month) {
      const [year, monthNum] = month.split('-');
      const start = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
      const end = new Date(parseInt(year), parseInt(monthNum), 0);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(end.toISOString().split('T')[0]);
    }
  };
  
  const handleReviewModeToggle = (enabled: boolean) => {
    setMonthlyReviewMode(enabled);
    if (enabled && !selectedMonth) {
      const now = new Date();
      const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      handleMonthChange(currentMonth);
      setSelectedMonth(currentMonth);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      draft: "status-draft",
      submitted: "status-submitted",
      locked: "status-locked",
    };
    
    return (
      <Badge className={variants[status] || ""}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString();
  };

  const formatUnit = (value: string | number | null | undefined, suffix: string, decimals: number = 2): string => {
    if (value == null) return '—';
    const num = parseFloat(value.toString());
    if (isNaN(num)) return '—';
    return `${num.toFixed(decimals)} ${suffix}`;
  };

  const formatGal = (value: string | number | null | undefined): string => {
    if (value == null) return '—';
    const num = parseFloat(value.toString());
    if (isNaN(num)) return '—';
    return `${num.toLocaleString()} gal`;
  };

  const hasChlorine = (log: any): boolean => {
    const chem = log.chemical;
    return !!(chem?.inchesUsedCl2 || chem?.gallonsDelivered || chem?.lbsCl2 || chem?.residual);
  };

  const isMissingResidual = (log: any): boolean => {
    return hasChlorine(log) && !log.chemical?.residual;
  };

  const isMissingClCalc = (log: any): boolean => {
    const chem = log.chemical;
    return !!chem?.inchesUsedCl2 && (!chem?.gallonsDelivered || !chem?.lbsCl2);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-bold">Database Viewer</h1>
                <p className="text-sm text-muted-foreground">Read-only daily log records</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container py-6">
        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Filters</CardTitle>
              <div className="flex items-center gap-4">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <HelpCircle className="w-4 h-4 mr-2" />
                      Legend
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Database Viewer Legend</DialogTitle>
                      <DialogDescription>Icons, status meanings, and system rules</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <h3 className="font-semibold mb-2">Status Meanings</h3>
                        <ul className="space-y-1 text-sm">
                          <li><Badge className="status-draft mr-2">Draft</Badge> - Log is editable and not yet submitted</li>
                          <li><Badge className="status-submitted mr-2">Submitted</Badge> - Log submitted for review, limited editing</li>
                          <li><Badge className="status-locked mr-2">Locked</Badge> - Log is locked for audit/compliance purposes</li>
                        </ul>
                      </div>
                      
                      <div>
                        <h3 className="font-semibold mb-2">Icons & Columns</h3>
                        <ul className="space-y-1 text-sm">
                          <li><Paperclip className="h-4 w-4 inline mr-2" /> - Number of photo attachments</li>
                          <li><FileText className="h-4 w-4 inline mr-2" /> - Number of field corrections</li>
                          <li><CheckCircle2 className="h-4 w-4 inline mr-2 text-green-600" /> - Log has been reviewed by supervisor</li>
                          <li><Clock className="h-4 w-4 inline mr-2 text-muted-foreground" /> - Log pending review</li>
                        </ul>
                      </div>
                      
                      <div>
                        <h3 className="font-semibold mb-2">Corrections Overlay Rule</h3>
                        <p className="text-sm text-muted-foreground">
                          Locked logs are immutable. Corrections are stored separately and applied when viewing/reporting.
                          Correction reasons never appear in state report PDFs.
                        </p>
                      </div>
                      
                      <div>
                        <h3 className="font-semibold mb-2">Review Process</h3>
                        <p className="text-sm text-muted-foreground">
                          Review notes are internal only and appear only in audit/support exports (not state board report).
                          Supervisors and admins can mark logs as reviewed to track monthly compliance work.
                        </p>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Monthly Review Mode Toggle */}
              <div className="flex items-center gap-4 pb-4 border-b">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="review-mode"
                    checked={monthlyReviewMode}
                    onCheckedChange={handleReviewModeToggle}
                  />
                  <Label htmlFor="review-mode" className="font-semibold">Monthly Review Mode</Label>
                </div>
                {monthlyReviewMode && (
                  <div className="flex items-center gap-2">
                    <Label htmlFor="month-picker">Month:</Label>
                    <Input
                      id="month-picker"
                      type="month"
                      value={selectedMonth}
                      onChange={(e) => handleMonthChange(e.target.value)}
                      className="w-40"
                    />
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <Label htmlFor="well-filter">Well</Label>
                <Select value={wellFilter} onValueChange={setWellFilter}>
                  <SelectTrigger id="well-filter">
                    <SelectValue placeholder="All Wells" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Wells</SelectItem>
                    {wells?.map((well) => (
                      <SelectItem key={well.id} value={well.id.toString()}>
                        {well.wellName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="status-filter">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger id="status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="locked">Locked</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="end-date">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="search"
                    placeholder="Well name, remarks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-4 border-t mt-4">
              <Badge
                variant={chlorineQuickFilter === 'has-chlorine' ? 'default' : 'outline'}
                className="cursor-pointer hover:bg-accent"
                onClick={() => setChlorineQuickFilter(chlorineQuickFilter === 'has-chlorine' ? null : 'has-chlorine')}
              >
                Has Cl
              </Badge>
              <Badge
                variant={chlorineQuickFilter === 'missing-residual' ? 'default' : 'outline'}
                className="cursor-pointer hover:bg-accent"
                onClick={() => setChlorineQuickFilter(chlorineQuickFilter === 'missing-residual' ? null : 'missing-residual')}
              >
                Missing Residual
              </Badge>
              <Badge
                variant={chlorineQuickFilter === 'missing-cl-calc' ? 'default' : 'outline'}
                className="cursor-pointer hover:bg-accent"
                onClick={() => setChlorineQuickFilter(chlorineQuickFilter === 'missing-cl-calc' ? null : 'missing-cl-calc')}
              >
                Missing Cl Calc
              </Badge>
              {chlorineQuickFilter && (
                <Badge
                  variant="secondary"
                  className="cursor-pointer"
                  onClick={() => setChlorineQuickFilter(null)}
                >
                  Clear Cl
                </Badge>
              )}
            </div>
            </div>
          </CardContent>
        </Card>

        {/* Review Summary Bar */}
        {monthlyReviewMode && reviewSummary && (
          <Card className="mb-6 bg-muted/30">
            <CardHeader>
              <CardTitle className="text-base">Review Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold">{reviewSummary.total}</div>
                  <div className="text-xs text-muted-foreground">Total Logs</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">{reviewSummary.reviewed}</div>
                  <div className="text-xs text-muted-foreground">Reviewed</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-orange-600">{reviewSummary.remaining}</div>
                  <div className="text-xs text-muted-foreground">Remaining</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{reviewSummary.hasCorrections}</div>
                  <div className="text-xs text-muted-foreground">Has Corrections</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{reviewSummary.missingAttachments}</div>
                  <div className="text-xs text-muted-foreground">Missing Attachments</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{reviewSummary.lockedCorrected}</div>
                  <div className="text-xs text-muted-foreground">Locked + Corrected</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Filter Chips */}
        {monthlyReviewMode && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-2">
                <Badge
                  variant={quickFilter === 'needs-review' ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setQuickFilter(quickFilter === 'needs-review' ? null : 'needs-review')}
                >
                  Needs Review
                </Badge>
                <Badge
                  variant={quickFilter === 'reviewed' ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setQuickFilter(quickFilter === 'reviewed' ? null : 'reviewed')}
                >
                  Reviewed
                </Badge>
                <Badge
                  variant={quickFilter === 'has-corrections' ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setQuickFilter(quickFilter === 'has-corrections' ? null : 'has-corrections')}
                >
                  Has Corrections
                </Badge>
                <Badge
                  variant={quickFilter === 'missing-attachments' ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setQuickFilter(quickFilter === 'missing-attachments' ? null : 'missing-attachments')}
                >
                  Missing Attachments
                </Badge>
                <Badge
                  variant={quickFilter === 'locked' ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setQuickFilter(quickFilter === 'locked' ? null : 'locked')}
                >
                  Locked
                </Badge>
                {quickFilter && (
                  <Badge
                    variant="secondary"
                    className="cursor-pointer"
                    onClick={() => setQuickFilter(null)}
                  >
                    Clear Filter
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Data Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Daily Log Records ({filteredLogs?.length || 0})</CardTitle>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 px-2">
                    <HelpCircle className="h-3 w-3 mr-1" />
                    Field Guide
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Field Explanations</DialogTitle>
                    <DialogDescription>Guide to calculated fields and chlorine values</DialogDescription>
                  </DialogHeader>
                  <div className="py-4 space-y-2 text-sm leading-relaxed">
                    <p className="italic text-xs text-muted-foreground p-2 bg-muted/30 rounded-sm mb-3">
                      Some fields may be blank if not entered or not applicable.
                    </p>
                    <div><strong>Hours:</strong> Total run time entered for the log date.</div>
                    <div><strong>GPM:</strong> Gallons per minute = gallons ÷ (hours × 60). Shown if both present. Ex: 100k gal ÷ 4h = ~417 GPM</div>
                    <div><strong>MG:</strong> Million gallons = gallons ÷ 1,000,000. Ex: 1M gal = 1.00 MG</div>
                    <div><strong>Cl Inches:</strong> Inches of chlorine solution used (tank level drop) entered by operator.</div>
                    <div><strong>Cl Gal:</strong> Estimated gallons delivered from inches used × gallons-per-inch from the active chlorine config (if available).</div>
                    <div><strong>Cl lbs:</strong> Estimated pounds of Cl₂ from inches used × gallons-per-inch × lbs Cl₂ per gallon (active config by date).</div>
                    <div><strong>Residual (ppm):</strong> Measured chlorine residual at the well/sample point (parts per million).</div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {filteredLogs && filteredLogs.length === 0 && (
              <div className="py-12 text-center">
                <p className="text-muted-foreground">No records found</p>
              </div>
            )}

            {filteredLogs && filteredLogs.length > 0 && (
              <div className="overflow-x-auto">
                <TooltipProvider>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 font-semibold">Well</th>
                      <th className="text-left p-2 font-semibold">Date</th>
                      <th className="text-left p-2 font-semibold">Status</th>
                      <th className="text-left p-2 font-semibold">Operator</th>
                      <th className="text-right p-2 font-semibold">Gallons</th>
                      <th className="text-right p-2 font-semibold">
                        MG
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="h-3 w-3 ml-1 inline opacity-60" />
                          </TooltipTrigger>
                          <TooltipContent>Million gallons = gallons ÷ 1,000,000. Ex: 1,000,000 gal = 1.00 MG</TooltipContent>
                        </Tooltip>
                      </th>
                      <th className="text-right p-2 font-semibold">Meters</th>
                      <th className="text-right p-2 font-semibold">
                        Hours/GPM
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="h-3 w-3 ml-1 inline opacity-60" />
                          </TooltipTrigger>
                          <TooltipContent>Hours: Total run time entered.<br/>GPM: Gallons/min = gallons ÷ (hours × 60). Shown if both present. Ex: 100k gal ÷ (4h × 60) ≈ 417 GPM</TooltipContent>
                        </Tooltip>
                      </th>
                      <th className="text-right p-2 font-semibold">
                        Cl Inches
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="h-3 w-3 ml-1 inline opacity-60" />
                          </TooltipTrigger>
                          <TooltipContent>Inches used from chlorine tank gauge</TooltipContent>
                        </Tooltip>
                      </th>
                      <th className="text-right p-2 font-semibold">
                        Cl Gal
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="h-3 w-3 ml-1 inline opacity-60" />
                          </TooltipTrigger>
                          <TooltipContent>Gallons delivered (inches × gal/inch config)</TooltipContent>
                        </Tooltip>
                      </th>
                      <th className="text-right p-2 font-semibold">
                        Cl lbs
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="h-3 w-3 ml-1 inline opacity-60" />
                          </TooltipTrigger>
                          <TooltipContent>Lbs Cl₂ (gallons × lbs/gal config)</TooltipContent>
                        </Tooltip>
                      </th>
                      <th className="text-right p-2 font-semibold">
                        Residual (ppm)
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="h-3 w-3 ml-1 inline opacity-60" />
                          </TooltipTrigger>
                          <TooltipContent>Tower residual chlorine level</TooltipContent>
                        </Tooltip>
                      </th>
                        <th className="text-left p-2 font-semibold">Remarks</th>
                        <th className="text-center p-2 font-semibold w-12">⚠️</th>
                        <th className="text-center p-2 font-semibold">
                          <Paperclip className="h-4 w-4 inline" />
                        </th>
                      <th className="text-center p-2 font-semibold">
                        <FileText className="h-4 w-4 inline" />
                      </th>
                      {monthlyReviewMode && (
                        <th className="text-center p-2 font-semibold">
                          <CheckCircle2 className="h-4 w-4 inline" />
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((log: any) => (
                      <tr
                        key={log.id}
                        className="border-b hover:bg-muted/50 cursor-pointer"
                        onClick={() => setSelectedLogId(log.id)}
                      >
                        <td className="p-2">{log.wellName}</td>
                        <td className="p-2">{formatDate(log.logDate)}</td>
                        <td className="p-2">{getStatusBadge(log.status)}</td>
                        <td className="p-2">{log.operatorInitial || "—"}</td>
                        <td className="p-2 text-right font-mono">
                          {log.gallonsPumped ? parseFloat(log.gallonsPumped).toLocaleString() : "—"}
                        </td>
                        <td className="p-2 text-right font-mono">
                          {log.gallonsPumped ? (parseFloat(log.gallonsPumped) / 1000000).toFixed(2) : "—"}
                        </td>
                        <td className="p-2 text-right font-mono text-xs">
                          {log.meterReadStart && log.meterReadEnd
                            ? `${log.meterReadStart} → ${log.meterReadEnd}`
                            : "—"}
                        </td>
                        <td className="p-2 text-right font-mono text-xs">
                          {log.hours && log.gpm
                            ? `${log.hours}h / ${log.gpm}`
                            : log.hours
                            ? `${log.hours}h`
                            : log.gpm
                            ? `${log.gpm} GPM`
                            : "—"}
                        </td>
                        <td className="p-2 text-right font-mono">
                          {formatUnit(log.chemical?.inchesUsedCl2, 'in')}
                        </td>
                        <td className="p-2 text-right font-mono">
                          {formatGal(log.chemical?.gallonsDelivered)}
                        </td>
                        <td className="p-2 text-right font-mono">
                          {formatUnit(log.chemical?.lbsCl2, 'lbs')}
                        </td>
                        <td className="p-2 text-right font-mono">
                          {formatUnit(log.chemical?.residual, 'ppm')}
                        </td>
                        <td className="p-2 max-w-xs truncate">
                          {log.remarks || "—"}
                        </td>
                        <td className="p-2 text-center w-12">
                          {(isMissingResidual(log) || isMissingClCalc(log)) && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <AlertCircle className="h-4 w-4 text-orange-500 mx-auto" />
                              </TooltipTrigger>
                              <TooltipContent>
                                {isMissingResidual(log) && <div>Missing residual ppm</div>}
                                {isMissingClCalc(log) && <div>Missing Cl calc (check config/inputs)</div>}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          {log.attachmentsCount > 0 ? (
                            <Badge variant="secondary" className="text-xs">
                              {log.attachmentsCount}
                            </Badge>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="p-2 text-center">
                          {log.correctionsCount > 0 ? (
                            <Badge variant="outline" className="text-xs text-orange-600 border-orange-600">
                              {log.correctionsCount}
                            </Badge>
                          ) : (
                            "—"
                          )}
                        </td>
                        {monthlyReviewMode && (
                          <td className="p-2 text-center">
                            {log.reviewed ? (
                              <Tooltip>
                                <TooltipTrigger>
                                  <CheckCircle2 className="h-4 w-4 text-green-600 inline" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs">
                                    Reviewed by {log.reviewedByName || `User #${log.reviewedBy}`}
                                    <br />
                                    {log.reviewedAt && new Date(log.reviewedAt).toLocaleString()}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <Clock className="h-4 w-4 text-muted-foreground inline" />
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                </TooltipProvider>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Detail Drawer */}
      <Sheet open={selectedLogId !== null} onOpenChange={(open) => !open && setSelectedLogId(null)}>
        <SheetContent className="sm:max-w-2xl overflow-y-auto">
          {selectedLog && (
            <>
              <SheetHeader>
                <SheetTitle>
                  {selectedLog.well?.wellName} - {formatDate(selectedLog.logDate)}
                </SheetTitle>
                <SheetDescription>
                  {getStatusBadge(selectedLog.status)}
                  {selectedLog.logTime && ` at ${selectedLog.logTime}`}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Basic Fields */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Log Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-muted-foreground">Operator:</span>
                        <p className="font-medium">{selectedLog.operatorInitial || "—"}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Status:</span>
                        <p className="font-medium">{selectedLog.status}</p>
                      </div>
                    </div>

                    <div>
                      <span className="text-muted-foreground">Meter Readings:</span>
                      <p className="font-mono">
                        Start: {selectedLog.meterReadStart || "—"} → End: {selectedLog.meterReadEnd || "—"}
                      </p>
                    </div>

                    <div>
                      <span className="text-muted-foreground">Gallons Pumped:</span>
                      <p className="font-mono text-lg">
                        {selectedLog.gallonsPumped ? parseFloat(selectedLog.gallonsPumped).toLocaleString() : "—"}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-muted-foreground">Hours:</span>
                        <p className="font-mono">{selectedLog.hours || "—"}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">GPM:</span>
                        <p className="font-mono">{selectedLog.gpm || "—"}</p>
                      </div>
                    </div>

                    {selectedLog.remarks && (
                      <div>
                        <span className="text-muted-foreground">Remarks:</span>
                        <p className="mt-1 text-sm whitespace-pre-wrap">{selectedLog.remarks}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Review Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Review Status</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {selectedLog.reviewed ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span className="font-medium">Reviewed</span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          <p>By: {selectedLog.reviewedByName || `User #${selectedLog.reviewedBy}`}</p>
                          <p>At: {selectedLog.reviewedAt && new Date(selectedLog.reviewedAt).toLocaleString()}</p>
                          {selectedLog.reviewNote && (
                            <div className="mt-2">
                              <p className="font-medium">Note:</p>
                              <p className="whitespace-pre-wrap">{selectedLog.reviewNote}</p>
                            </div>
                          )}
                        </div>
                        
                        {user && (user.role === 'supervisor' || user.role === 'admin') && (
                          <div className="mt-4 pt-4 border-t space-y-2">
                            <Label htmlFor="review-note-update">Update Review Note</Label>
                            <Textarea
                              id="review-note-update"
                              placeholder="Add or update internal review note (optional)"
                              value={reviewNote}
                              onChange={(e) => setReviewNote(e.target.value)}
                              rows={3}
                            />
                            <Button
                              size="sm"
                              onClick={() => {
                                markReviewedMutation.mutate({
                                  dailyLogId: selectedLog.id,
                                  reviewNote: reviewNote || undefined,
                                });
                              }}
                              disabled={markReviewedMutation.isPending}
                            >
                              {markReviewedMutation.isPending ? 'Updating...' : 'Update Review'}
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          <span>Not yet reviewed</span>
                        </div>
                        
                        {user && (user.role === 'supervisor' || user.role === 'admin') && (
                          <div className="space-y-2">
                            <Label htmlFor="review-note">Review Note (optional)</Label>
                            <Textarea
                              id="review-note"
                              placeholder="Add internal review note (optional)"
                              value={reviewNote}
                              onChange={(e) => setReviewNote(e.target.value)}
                              rows={3}
                            />
                            <Button
                              size="sm"
                              onClick={() => {
                                markReviewedMutation.mutate({
                                  dailyLogId: selectedLog.id,
                                  reviewNote: reviewNote || undefined,
                                });
                              }}
                              disabled={markReviewedMutation.isPending}
                            >
                              {markReviewedMutation.isPending ? 'Marking...' : 'Mark Reviewed'}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Attachments */}
                {selectedLog.attachments && selectedLog.attachments.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Attachments ({selectedLog.attachments.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <PhotoGallery dailyLogId={selectedLog.id} canDelete={false} />
                    </CardContent>
                  </Card>
                )}

                {/* Corrections History */}
                {selectedLog.corrections && selectedLog.corrections.length > 0 && (
                  <CorrectionsHistory corrections={selectedLog.corrections} />
                )}

                {/* Custom Fields */}
                {selectedLog.extraJson && typeof selectedLog.extraJson === 'object' && Object.keys(selectedLog.extraJson).length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Custom Fields</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <dl className="space-y-2 text-sm">
                        {Object.entries(selectedLog.extraJson as Record<string, any>).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <dt className="text-muted-foreground">{key}:</dt>
                            <dd className="font-medium">{String(value)}</dd>
                          </div>
                        ))}
                      </dl>
                    </CardContent>
                  </Card>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
